import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { IPaginatedResponse } from 'app/projects/subentity/src/lib/interfaces/paginated-response.interface';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICourseCatalogLandingPageOverviewQuery } from '../interfaces/course-catalog-landing-page-overview.interface';
import { ICourseCatalogLandingPageQuery } from '../interfaces/course-catalog-landing-page.query.interface';
import { ICourseCatalogPostingStreamQuery } from '../interfaces/course-catalog-posting-stream-query.interface';
import { CourseCatalogLandingPageOverview } from '../models/course-catalog-landing-page-overview.model';
import { CourseCatalogLandingPageCommand } from '../models/course-catalog-landing-page.command.model';
import { CourseCatalogLandingPage } from '../models/course-catalog-landing-page.model';
import { CourseCatalogPostingStream } from '../models/course-catalog-posting-stream.model';

export interface Pagination {
    numberOfPages: number;
    numberOfResults: number;
}

@Injectable()
export class CourseCatalogLandingPageApiService extends EntityService<ICourseCatalogLandingPageQuery> {
    postingsPage = 1;

    private _postingsPagination: Pagination = { numberOfPages: 0, numberOfResults: 0 };

    get postingsPagination(): Pagination {
        return this._postingsPagination;
    }
    set postingsPagination(value: Pagination) {
        this._postingsPagination = value;
    }

    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) private environment: IEnvironment) {
        super(http, environment.apiUrl, 'catalog');
    }

    getCourseCatalogLandingPage(categoryId: string): Observable<CourseCatalogLandingPage> {
        return this.http.get<ICourseCatalogLandingPageQuery>(`${this.apiUrl}/categories/${categoryId}/landing-page`).pipe(map((query) => new CourseCatalogLandingPage(query)));
    }

    getCourseCatalogLandingPageOverview(categoryId: string): Observable<CourseCatalogLandingPageOverview> {
        return this.http
            .get<ICourseCatalogLandingPageOverviewQuery>(`${this.apiUrl}/categories/${categoryId}/landing-page/overview`)
            .pipe(map((query) => new CourseCatalogLandingPageOverview(query)));
    }

    getCategoryPostingStream(categoryId: string): Observable<CourseCatalogPostingStream[]> {
        return this.http
            .get<ICourseCatalogPostingStreamQuery[]>(`${this.apiUrl}/categories/${categoryId}/posting-stream`)
            .pipe(map((queries) => queries.map((query) => new CourseCatalogPostingStream(query))));
    }

    getCategoryPostings(categoryId: string, limit?: number, search?: string, postingType?: string): Observable<CourseCatalogPostingStream[]> {
        let params = new HttpParams().set('filter[enabled]', 'course_offering');

        if (limit) {
            params = params.set('limit', limit.toString());
        }

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (postingType) {
            params = params.set('filter[type]', postingType);
        }

        return this.http.get<IPaginatedResponse<ICourseCatalogPostingStreamQuery>>(`${this.apiUrl}/categories/${categoryId}/postings`, { params }).pipe(
            map((response) => {
                return response.data.map((query) => new CourseCatalogPostingStream(query));
            })
        );
    }

    getCategoryWithOfferingsPostings(
        categoryId: string,
        search = '',
        pageSize = 100,
        pageIndex = 1,
        language?: string,
        postingType?: string,
        extendedFilters?: Record<string, any>
    ): Observable<CourseCatalogPostingStream[]> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (language) {
            params = params.set('filter[language]', language);
        }

        if (postingType) {
            params = params.set('filter[type]', postingType);
        }

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (extendedFilters) {
            Object.entries(extendedFilters).forEach(([key, value]) => {
                const filterKey = this.mapFilterKey(key);

                if (Array.isArray(value)) {
                    value.forEach((v) => {
                        params = params.append(filterKey, v);
                    });
                } else if (value !== null && value !== undefined && value !== '') {
                    params = params.set(filterKey, value);
                }
            });
        }

        return this.http.get<IPaginatedResponse<ICourseCatalogPostingStreamQuery>>(`${this.apiUrl}/categories/${categoryId}/postings`, { params }).pipe(
            map((query) => {
                this.postingsPagination = {
                    numberOfPages: query.pagination.number_of_pages,
                    numberOfResults: query.pagination.number_of_results,
                };
                return query.data.map((item) => new CourseCatalogPostingStream(item));
            })
        );
    }

    updateCourseCatalogLandingPage(landingPageId: string, entity: CourseCatalogLandingPage): Observable<CourseCatalogLandingPage> {
        const data = new CourseCatalogLandingPageCommand(entity);
        return this.http
            .patch<ICourseCatalogLandingPageQuery>(`${this.apiUrl}/category-landing-pages/${landingPageId}`, data)
            .pipe(map((query) => new CourseCatalogLandingPage(query)));
    }

    updateCourseCatalogLandingPageStatus(landingPageId: string, status: string): Observable<CourseCatalogLandingPage> {
        const data = { status };
        return this.http
            .patch<ICourseCatalogLandingPageQuery>(`${this.apiUrl}/category-landing-pages/${landingPageId}/status`, data)
            .pipe(map((query) => new CourseCatalogLandingPage(query)));
    }

    private mapFilterKey(key: string): string {
        if (key.endsWith('_min')) {
            return `filter[${key.replace(/_min$/, '')}][gte]`;
        }

        if (key.endsWith('_max')) {
            return `filter[${key.replace(/_max$/, '')}][lte]`;
        }

        return `filter[${key}]`;
    }
}
